import { sendSmsNodeDataSchema } from '@repo/shared-types';

import type { PostCallWorkflow, SendSmsNodeData } from '@repo/shared-types';

export const getWhatsAppFallbackErrors = (data: Partial<SendSmsNodeData>): string[] => {
  if (!data.waSmsFallback) return [];
  const errors: string[] = [];
  if (data.provider !== 'whatsapp' || data.waMessageType === 'contacts') {
    errors.push(
      'Automatic SMS fallback requires a WhatsApp template. Turn it off or select WhatsApp and Template.',
    );
  }
  if (!data.waTemplateName?.trim() || !data.waFromNumberId?.trim()) {
    errors.push('Select a WhatsApp template and sender before using automatic fallback.');
  }
  if (data.sendAt)
    errors.push(
      'Automatic SMS fallback only supports immediate sending. Clear the schedule or turn fallback off.',
    );

  const fields = {
    from: 'SMS sender',
    body: 'SMS message',
    key: 'Unique request/car key',
    consent: 'Consent expression',
  } as const;
  const fallback = data.waSmsFallback;
  for (const [field, label] of Object.entries(fields)) {
    if (!fallback[field as keyof typeof fields]?.trim()) errors.push(`${label} is required.`);
  }
  if (!sendSmsNodeDataSchema.shape.waSmsFallback.safeParse(fallback).success && !errors.length) {
    errors.push('Choose a supported SMS provider and complete the fallback settings.');
  }
  if (
    fallback.from.trim() &&
    !fallback.from.includes('{{') &&
    !/^\+[1-9]\d{7,14}$/.test(fallback.from.trim())
  ) {
    errors.push('SMS sender must be an international number (for example +447…) or a variable.');
  }
  if (
    fallback.consent.trim() &&
    !fallback.consent.includes('{{') &&
    !/^(true|false)$/i.test(fallback.consent.trim())
  ) {
    errors.push('Consent must be true, false, or a variable resolving to one of those values.');
  }
  return errors;
};

export const getWorkflowFallbackErrors = (workflow: PostCallWorkflow | null): string[] =>
  (workflow?.nodes ?? []).flatMap((node) =>
    node.type === 'send_sms'
      ? getWhatsAppFallbackErrors(node.data as Partial<SendSmsNodeData>).map(
          (error) => `${node.data.label || node.id}: ${error}`,
        )
      : [],
  );
